import type { ArticleCategory, ArticleLocale } from '../../lib/api/knowledge';

/**
 * Every `t()` key this module needs for the closed vocabularies
 * `knowledge.ts` carries — the identical shape `justificationLabels.ts`
 * documents at length: `as const satisfies Record<...>` is what keeps a
 * template-literal key (`` t(`knowledge.category.${x}`) ``) out of this
 * codebase. That form compiles but fails `npm run typecheck` against the
 * typed `t()` overloads `i18next.d.ts` installs, because TypeScript widens
 * the template to `string`, which is not a key `en.json`'s shape admits.
 * These objects are the one place each vocabulary is mapped to a literal
 * key, so every call site reads a literal rather than building one.
 */

/** The four fixed values `StoreArticleRequest`/`UpdateArticleRequest` allow (spec K-3). A ROW's own category — no `all` member. */
export const CATEGORY_LABEL_KEY = {
  sensor: 'knowledge.category.sensor',
  replacement: 'knowledge.category.replacement',
  defect: 'knowledge.category.defect',
  other: 'knowledge.category.other',
} as const satisfies Record<ArticleCategory, string>;

/** The category segmented control offers five values — the four plus `all` — unlike `CATEGORY_LABEL_KEY` above, which types only a ROW's own category (spec K-9: "the `empty` sentinel for 'all categories'" is dropped; this is a real absent-filter state, not a fifth stored value). */
export type ArticleCategoryFilter = ArticleCategory | 'all';

export const CATEGORY_FILTER_LABEL_KEY = {
  all: 'knowledge.categoryFilter.all',
  sensor: 'knowledge.category.sensor',
  replacement: 'knowledge.category.replacement',
  defect: 'knowledge.category.defect',
  other: 'knowledge.category.other',
} as const satisfies Record<ArticleCategoryFilter, string>;

export const LOCALE_LABEL_KEY = {
  en: 'knowledge.locale.en',
  ar: 'knowledge.locale.ar',
} as const satisfies Record<ArticleLocale, string>;

/** The Draft/Published filter, gated on `knowledge.manage` (spec §3). */
export type PublishedFilter = 'all' | 'published' | 'draft';

export const PUBLISHED_FILTER_LABEL_KEY = {
  all: 'knowledge.publishedFilter.all',
  published: 'knowledge.publishedFilter.published',
  draft: 'knowledge.publishedFilter.draft',
} as const satisfies Record<PublishedFilter, string>;
